"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar as CalendarIcon, Clock, Mail, MapPin, Phone, Loader } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState, useEffect, useRef } from "react";
import { useForm, FormProvider as RHFProvider } from "react-hook-form";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const timeSlots = ["09:00 – 11:00", "11:00 – 13:00", "14:00 – 16:00", "16:00 – 18:00"];
const suitTypesFr = ["Soutien Administratif", "Littératie Numérique", "Aide Alimentaire", "Activités Sociales"];
const suitTypesEn = ["Administrative Support", "Digital Literacy", "Food Assistance", "Social Activities"];
interface FormValues {
  name: string;
  email: string;
  phone: string;
  fitting_date: string;
  time_slot: string;
  suit_type: string;
  notes: string;
}
function FittingBookingForm({
  isEnglish
}: {
  isEnglish: boolean;
}) {
  const [formDef, setFormDef] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [honeypotVal, setHoneypotVal] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showCalendar, setShowCalendar] = useState(false);
  const fetchedAtRef = useRef<number>(Date.now());
  const methods = useForm<FormValues>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      fitting_date: "",
      time_slot: "",
      suit_type: "",
      notes: ""
    }
  });
  const {
    control,
    handleSubmit,
    reset
  } = methods;
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    client.pluginRequest("wvc-forms/v1/forms/contact_form_join_us").then((res: any) => {
      if (isMounted && res) {
        setFormDef(res);
        fetchedAtRef.current = Date.now();
      }
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listRes: any) => {
          if (!isMounted || !listRes?.items) return;
          const items = listRes.items;
          const matched = items.find((i: any) => i.key === "contact_form_join_us") || (items.length === 1 ? items[0] : null) || items.find((i: any) => i.name?.toLowerCase().includes("contact") || i.name?.toLowerCase().includes("rejoindre") || i.name?.toLowerCase().includes("join"));
          if (matched?.key) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          }
        }).then((res: any) => {
          if (isMounted && res) {
            setFormDef(res);
            fetchedAtRef.current = Date.now();
          }
        }).catch(() => {});
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const timeSlotField = formDef?.fields?.find((f: any) => f.key === "time_slot");
  const timeSlotOptions: {
    value: string;
    label: string;
  }[] = timeSlotField?.options && timeSlotField.options.length > 0 ? timeSlotField.options : timeSlots.map(slot => ({
    value: slot,
    label: slot
  }));
  const suitTypeField = formDef?.fields?.find((f: any) => f.key === "suit_type");
  const activeSuitTypes = isEnglish ? suitTypesEn : suitTypesFr;
  const suitTypeOptions: {
    value: string;
    label: string;
  }[] = suitTypeField?.options && suitTypeField.options.length > 0 ? suitTypeField.options : activeSuitTypes.map(type => ({
    value: type,
    label: type
  }));
  const resetForm = () => {
    reset({
      name: "",
      email: "",
      phone: "",
      fitting_date: "",
      time_slot: "",
      suit_type: "",
      notes: ""
    });
    setSelectedDate(undefined);
    setIsSubmitted(false);
    setSubmitError(null);
  };
  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        setSubmitError(isEnglish ? "An error occurred. Please try again." : "Une erreur est survenue. Veuillez réessayer.");
        setIsSubmitting(false);
        return;
      }
      const minSeconds = formDef?.spam?.min_seconds ?? 3;
      const elapsed = (Date.now() - fetchedAtRef.current) / 1000;
      if (elapsed < minSeconds) {
        await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsed) * 1000));
      }
      const payloadValues: Record<string, any> = {};
      const keys: (keyof FormValues)[] = ["name", "email", "phone", "fitting_date", "time_slot", "suit_type", "notes"];
      keys.forEach(key => {
        if (data[key] !== undefined && data[key] !== "") {
          payloadValues[key] = data[key];
        }
      });
      const formKey = formDef?.key || "contact_form_join_us";
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
        method: "POST",
        data: {
          values: payloadValues,
          token: formDef?.spam?.token,
          hp: formDef?.spam?.honeypot ? {
            [formDef.spam.honeypot.field]: honeypotVal
          } : undefined,
          meta: {
            source_url: window.location.href,
            referrer: document.referrer
          }
        }
      });
      if (res?.success) {
        setIsSubmitted(true);
        if (res.confirmation?.message) {
          const cleanText = res.confirmation.message.replace(/<[^>]*>/g, " ").trim();
          setSuccessMessage(cleanText || (isEnglish ? "Your request has been successfully received." : "Votre demande a été reçue avec succès."));
        } else {
          setSuccessMessage(isEnglish ? "Your request has been received. We will contact you within 24 hours to confirm the details." : "Votre demande a été reçue. Nous vous contacterons dans les 24 heures pour confirmer les détails.");
        }
      } else if (res?.code === "frms_validation_failed") {
        const firstError = res.errors ? Object.values(res.errors)[0] as any : null;
        setSubmitError(firstError?.message || res.message || (isEnglish ? "Please check the information entered." : "Veuillez vérifier les informations saisies."));
      } else {
        setSubmitError(res?.message || (isEnglish ? "An error occurred. Please try again." : "Une erreur est survenue. Veuillez réessayer."));
      }
    } catch (err: any) {
      setSubmitError(err?.message || (isEnglish ? "An error occurred. Please try again or contact us directly." : "Une erreur est survenue. Veuillez réessayer ou nous contacter."));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-xl bg-accent">
        <p className="font-default text-2xl text-foreground mb-4">
          {isEnglish ? "Thank you very much." : "Merci beaucoup."}
        </p>
        <p className="text-muted-foreground mb-6 italic">
          {successMessage || (isEnglish ? "Your request has been received. We will contact you within 24 hours to confirm the details." : "Votre demande a été reçue. Nous vous contacterons dans les 24 heures pour confirmer les détails.")}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-2xl px-6">
          {isEnglish ? "Make another request" : "Faire une autre demande"}
        </Button>
      </div>;
  }
  return <RHFProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
        {formDef?.spam?.honeypot && <input type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} style={{
        position: "absolute",
        left: "-9999px"
      }} tabIndex={-1} autoComplete="off" aria-hidden="true" />}

        <FormField control={control} name="name" rules={{
        required: isEnglish ? "Name is required" : "Le nom est requis",
        minLength: {
          value: 2,
          message: isEnglish ? "Min 2 characters" : "Min 2 caractères"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Full Name" : "Nom Complet"}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder={isEnglish ? "John Tremblay" : "Jean Tremblay"} {...field} className="border border-border bg-transparent px-4 h-12 rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: isEnglish ? "Email is required" : "Le courriel est requis",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: isEnglish ? "Invalid email" : "Courriel invalide"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Email Address" : "Adresse Courriel"}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder={isEnglish ? "john@example.com" : "jean@exemple.com"} {...field} className="border border-border bg-transparent px-4 h-12 rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: isEnglish ? "Phone is required" : "Le téléphone est requis"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Phone Number" : "Numéro de Téléphone"}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (780) 000-0000" {...field} className="border border-border bg-transparent px-4 h-12 rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="fitting_date" rules={{
        required: isEnglish ? "Please select a date" : "Veuillez choisir une date"
      }} render={({
        field
      }) => <FormItem className="gap-3 relative">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Preferred Appointment Date" : "Date de Rendez-vous Souhaitée"}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Button type="button" variant="ghost" onClick={() => setShowCalendar(!showCalendar)} className="w-full justify-between h-12 border border-border bg-transparent px-4 rounded-2xl text-base hover:bg-transparent focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary font-normal">
                    <span className={selectedDate ? "text-foreground" : "text-muted-foreground"}>
                      {selectedDate ? selectedDate.toLocaleDateString(isEnglish ? "en-CA" : "fr-CA", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                }) : isEnglish ? "Choose a date" : "Choisir une date"}
                    </span>
                    <SafeHydrate><CalendarIcon className="size-4 text-muted-foreground" /></SafeHydrate>
                  </Button>
                  {showCalendar && <div className="absolute z-50 mt-2 bg-popover border border-border rounded-xl shadow-lg">
                      <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={date => {
                  setSelectedDate(date);
                  field.onChange(date ? date.toISOString().split("T")[0] : "");
                  setShowCalendar(false);
                }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} /></SafeHydrate>
                    </div>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="time_slot" rules={{
        required: isEnglish ? "Please choose a time slot" : "Veuillez choisir un créneau"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Preferred Time Slot" : "Créneau Horaire Préféré"}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="border border-border bg-transparent px-4 h-12 w-full rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base">
                    <SelectValue placeholder={isEnglish ? "Choose a time" : "Choisir une heure"} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlotOptions.map((opt, i) => <SelectItem data-index={i} key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="suit_type" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Type of Support" : "Type de Soutien"}
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="border border-border bg-transparent px-4 h-12 w-full rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base">
                    <SelectValue placeholder={isEnglish ? "Select a service (optional)" : "Sélectionner un service (optionnel)"} />
                  </SelectTrigger>
                  <SelectContent>
                    {suitTypeOptions.map((opt, i) => <SelectItem data-index={i} key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="notes" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {isEnglish ? "Tell us about your needs" : "Parlez-nous de vos besoins"}
              </FormLabel>
              <FormControl>
                <Textarea rows={4} placeholder={isEnglish ? "How can we help you today?" : "Comment pouvons-nous vous aider aujourd'hui ?"} {...field} className="border border-border bg-transparent p-4 rounded-xl focus-visible:ring-1 focus-visible:ring-secondary focus-visible:border-secondary text-base resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 border border-destructive/30 rounded-xl bg-destructive/5">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <div className="pt-4 space-y-4">
          <Button type="submit" disabled={isSubmitting} className="px-10 py-6 h-auto text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg transition-all duration-300 rounded-2xl">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                {isEnglish ? "Sending..." : "Envoi en cours..."}
              </> : <>
                {isEnglish ? "Request an Appointment" : "Demander un Rendez-vous"}
                <span className="ml-2">→</span>
              </>}
          </Button>
          <p className="text-sm italic text-muted-foreground">
            {isEnglish ? "We usually reply within 24 hours." : "Nous répondons généralement sous 24 heures."}
          </p>
        </div>
      </Form>
    </RHFProvider>;
}
export default function EntronsEnContact() {
  const [language, setLanguage] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("lacolonne_language") || "fr";
    }
    return "fr";
  });
  useEffect(() => {
    const updateLang = () => {
      const saved = localStorage.getItem("lacolonne_language") || "fr";
      setLanguage(saved);
    };
    window.addEventListener("storage", updateLang);
    window.addEventListener("languagechange", updateLang);
    return () => {
      window.removeEventListener("storage", updateLang);
      window.removeEventListener("languagechange", updateLang);
    };
  }, []);
  const isEnglish = language === "en";
  const intro = useRevealOnScroll<HTMLDivElement>();
  const formCol = useRevealOnScroll<HTMLDivElement>();
  const atelierCard = useRevealOnScroll<HTMLDivElement>();
  const workshopDetails = [{
    icon: "map",
    label: isEnglish ? "Address" : "Adresse",
    lines: ["Association La Colonne", "Edmonton, Alberta", isEnglish ? "Community Office" : "Bureau communautaire"]
  }, {
    icon: "clock",
    label: isEnglish ? "Hours" : "Heures",
    lines: [isEnglish ? "Monday – Friday" : "Lundi – Vendredi", "09:00 – 17:00", isEnglish ? "By appointment" : "Sur rendez-vous"]
  }, {
    icon: "phone",
    label: isEnglish ? "Phone" : "Téléphone",
    lines: [isEnglish ? "Contact our team" : "Contactez notre équipe"]
  }, {
    icon: "mail",
    label: isEnglish ? "Email" : "Courriel",
    lines: ["info@lacolonne.org"]
  }];
  return <section data-section-id="3689" id="entrons-en-contact" className="bg-background text-foreground pt-28 md:pt-36 pb-20 md:pb-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div ref={intro.ref} className={`text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-xs uppercase tracking-[0.3em] text-secondary mb-8 font-mono">
            {isEnglish ? "Contact & Support" : "Contact & Soutien"}
          </p>
          <h2 className="font-default text-5xl md:text-7xl lg:text-[88px] leading-[1.05] text-foreground mb-8">
            {isEnglish ? <>
                Get in <em className="text-secondary font-serif italic font-normal">Touch</em>
              </> : <>
                Entrons en <em className="text-secondary font-serif italic font-normal">Contact</em>
              </>}
          </h2>
          <p className="text-base md:text-[17px] leading-relaxed text-muted-foreground max-w-[480px] mx-auto">
            {isEnglish ? "Book a personalized support session. Each meeting is a pillar of support for your integration and well-being within our community." : "Réservez une session d'accompagnement personnalisée. Chaque rencontre est un pilier de soutien pour votre intégration et votre bien-être au sein de notre communauté."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div ref={formCol.ref} className={`lg:col-span-3 transition-all duration-1000 ease-out ${formCol.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FittingBookingForm isEnglish={isEnglish} />
          </div>

          <div ref={atelierCard.ref} className={`lg:col-span-2 transition-all duration-1000 ease-out delay-150 ${atelierCard.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="bg-accent rounded-md p-10 md:p-14 lg:sticky lg:top-28 border border-border">
              <p className="text-xs uppercase tracking-[0.3em] text-secondary mb-6 font-mono">
                La Colonne
              </p>
              <h3 className="font-default text-3xl md:text-[32px] leading-tight text-foreground mb-10">
                {isEnglish ? "Come Visit Us" : "Venez nous voir"}
              </h3>

              <div className="space-y-8">
                {workshopDetails.map((detail, i) => <div data-index={i} key={detail.label} className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {detail.icon === "map" && <MapPin className="size-4 text-secondary" strokeWidth={1.5} />}
                      {detail.icon === "clock" && <Clock className="size-4 text-secondary" strokeWidth={1.5} />}
                      {detail.icon === "phone" && <Phone className="size-4 text-secondary" strokeWidth={1.5} />}
                      {detail.icon === "mail" && <Mail className="size-4 text-secondary" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-mono">
                        {detail.label}
                      </p>
                      <div className="text-[15px] text-foreground leading-relaxed">
                        {detail.lines.map((line, j) => <p data-index={j} key={j}>{line}</p>)}
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-10 pt-10 border-t border-border">
                <div className="rounded-md overflow-hidden mb-3 bg-muted aspect-[16/10] border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/edmonton_community_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/edmonton_community_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/edmonton_community_map.png-wvc-sizes" alt={isEnglish ? "Location map of Association La Colonne in Edmonton" : "Carte de localisation de l'Association La Colonne à Edmonton"} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                </div>
                <Link to="#" className="text-sm text-foreground italic hover:text-secondary transition-colors duration-300 inline-flex items-center gap-1 font-serif">
                  {isEnglish ? "Open in Google Maps" : "Ouvrir dans Google Maps"}
                  <span>→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}